import { useEffect, useState } from "react";
import { LoadingBlock } from "../components/Spinner";
import { api } from "../api/client";
import { useExperience } from "../context/ExperienceContext";
import { FrameworkControlList } from "../components/FrameworkControlList";
import type { SoaRow, EvidenceDoc } from "../components/FrameworkControlList";
import { buildInsightsMap } from "../components/ControlInsightsPanel";
import type { ControlInsights } from "../components/ControlInsightsPanel";
import { StandardHeader, STANDARD_PAGE_LABELS } from "../components/standards/StandardHeader";
import { isoClauseGroups } from "../components/standards/isoClauses";

// ISO/IEC 27001:2022: the required clauses (4 to 10) and the Annex A controls, each on the
// shared assessment list (components/FrameworkControlList.tsx). Clauses group by clause number;
// Annex A groups by its four themes (Control.domain).

interface Props { tab: "clauses" | "controls" }

export function Iso27001Page({ tab }: Props) {
  const { novice } = useExperience();
  const [clauseRows, setClauseRows] = useState<SoaRow[] | null>(null);
  const [controlRows, setControlRows] = useState<SoaRow[] | null>(null);
  const [docs, setDocs] = useState<EvidenceDoc[]>([]);
  const [insightsMap, setInsightsMap] = useState<Map<string, ControlInsights>>(new Map());

  async function loadAll() {
    const [soaData, docsData, latestRun] = await Promise.all([
      api.get<{ soa: SoaRow[] }>("/controls/soa"),
      api.get<{ documents: EvidenceDoc[] }>("/documents"),
      api.get<{ run: { resultJson: unknown } | null }>("/guidance/latest").catch(() => ({ run: null })),
    ]);
    const iso = soaData.soa.filter((r) => r.control.frameworkId === "iso27001");
    setClauseRows(iso.filter((r) => r.control.kind === "CLAUSE"));
    setControlRows(iso.filter((r) => r.control.kind === "CONTROL"));
    setDocs(docsData.documents);
    if (latestRun.run?.resultJson) setInsightsMap(buildInsightsMap(latestRun.run.resultJson));
  }

  useEffect(() => { loadAll(); }, []);

  const rows = tab === "clauses" ? clauseRows : controlRows;
  if (rows === null) return <LoadingBlock />;

  return (
    <div className="page-pad">
      {tab === "clauses" ? (
        <>
          <StandardHeader
            frameworkId="iso27001"
            page={STANDARD_PAGE_LABELS.requiredClauses}
            subtitle="Assess how the organisation meets each of the required clauses 4 to 10 and attach the evidence."
          />
          <FrameworkControlList
            rows={rows}
            docs={docs}
            frameworkId="iso27001"
            loadAll={loadAll}
            novice={novice}
            insightsMap={insightsMap}
            groups={isoClauseGroups()}
            noun="clause"
            groupLabel="All clauses"
            guidedIntro={{
              title: "Working through the required clauses",
              text: "ISO 27001 has a set of required clauses (4 to 10). Open each one, record how your organisation meets it and attach the evidence.",
            }}
          />
        </>
      ) : (
        <>
          <StandardHeader
            frameworkId="iso27001"
            page={STANDARD_PAGE_LABELS.annexA}
            subtitle="Assess each of the 93 Annex A controls across its four themes (A.5 to A.8) and attach the evidence."
          />
          <FrameworkControlList
            rows={rows}
            docs={docs}
            frameworkId="iso27001"
            loadAll={loadAll}
            novice={novice}
            insightsMap={insightsMap}
            groupLabel="All themes"
          />
        </>
      )}
    </div>
  );
}
